import { t } from '@lingui/core/macro';
import { compact, includes, isEmpty } from 'lodash';
import type { ChangeEvent } from 'react';
import { toast } from 'react-toastify';
import { _1_MB_IN_BYTES } from '../constants';

export const getUploadedFile = (e: ChangeEvent<HTMLInputElement>): File | undefined => {
  const files = e.target.files;

  if (!files || isEmpty(files) || files.length === 0) return;
  return files[0];
};

export const parseAcceptedExtensions = (acceptedExtensions?: string): string[] | undefined =>
  compact(acceptedExtensions?.replace(/,/g, '').split('.'));

export const isFileEmpty = (file: File): boolean => !file || file.size === 0;

export const DEFAULT_MAX_FILE_SIZE_IN_MB = 10;

export const isFileTooLarge = (file: File, maxSizeInMB = DEFAULT_MAX_FILE_SIZE_IN_MB): boolean =>
  file.size > maxSizeInMB * _1_MB_IN_BYTES;

export const hasGoodExtension = (file: File, acceptedExtensions?: string) => {
  const extensionsToUse = parseAcceptedExtensions(acceptedExtensions);
  const splittedFileType = file.name.split('.');
  const fileExtension = splittedFileType[splittedFileType.length - 1];

  if (!acceptedExtensions) return true;

  // Use file type and file name to know which type is the file
  // file.type is sometime not dev friendly (and not accepted by 'accept' props of input). In this case,
  // we compare with file.name (not everytime here, this is why we keep the file.type comparison).
  // Ex: tab-separated-values vs .tsv
  if (
    extensionsToUse &&
    !includes(extensionsToUse, file.type.split('/')[1]) &&
    !includes(extensionsToUse, fileExtension)
  ) {
    toast.error(t`Votre fichier doit être de type : ${extensionsToUse.join(', ')}`);
    return false;
  }
  return true;
};

export const isFileValid = (
  file: File,
  acceptedExtensions?: string,
  maxFileSizeInMB = DEFAULT_MAX_FILE_SIZE_IN_MB
) => {
  if (isFileEmpty(file)) {
    toast.error(t`Erreur de chargement : les fichiers vides ne sont pas acceptés.`);
    return false;
  }

  if (isFileTooLarge(file, maxFileSizeInMB)) {
    toast.error(
      t`Erreur de chargement : le fichier est trop volumineux (> ${maxFileSizeInMB} MB).`
    );
    return false;
  }

  return hasGoodExtension(file, acceptedExtensions);
};

type DownloadTextFileParams = {
  fileName: string;
  content: string;
  mimeType: string; // example: 'text/csv' — note: don't include charset here, it will be added automatically
};

/**
 * Downloads a text file with the given content and mime type.
 *
 * Usage example:
 *    <DownloadButton
 *       onClick={() =>
 *         downloadTextFile({
 *           fileName: 'myfile.csv',
 *           content: 'your,file,content',
 *           mimeType: 'text/csv',
 *         })
 *       }
 *     />
 */
export const downloadTextFile = ({ fileName, content, mimeType }: DownloadTextFileParams) => {
  const BOM = '\uFEFF';
  const blob = new Blob([`${BOM}${content}`], { type: `${mimeType};charset=utf-8;` });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', fileName);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
